/**
 * Play GBUX session + enter/leave best practices.
 *
 * **Play GBUX = real GBUX for poker.grudge-studio.com** (not fake chips).
 * “Play” = in-play on this dApp (edge D1 ledger). Funded by:
 * - third-party wallet SPL transfer → agent → play 1:1
 * - purchase SOL/USDT @ $0.001/GBUX via AI agent
 * - agent house/AI systems (BJ bank, AI stacks, rake)
 *
 * Layers:
 * - Wallet connection (Phantom/inject) — until disconnect
 * - X-Play-Session (HMAC, default 24h) — one sign for tables/slots/BJ
 * - Play ledger (D1) — real GBUX; join debits, leave credits; slots/BJ bet/payout
 *
 * Never re-sign Web3 for navigation if session still valid.
 */
import {
  clearPlaySession,
  playSessionHeaders,
  readPlaySession,
  writePlaySession,
} from "@/lib/play-session";

export const PLAY_SESSION_DEFAULT_HOURS = 24;

export type PlaySessionStatus = {
  active: boolean;
  exp: number | null;
  remainingSec: number;
  ttlDefaultHours: number;
  localToken: boolean;
};

/** Silent GET — validates stored token with edge; updates exp. No wallet popup. */
export async function warmPlaySession(
  wallet: string | null | undefined,
): Promise<PlaySessionStatus> {
  const empty: PlaySessionStatus = {
    active: false,
    exp: null,
    remainingSec: 0,
    ttlDefaultHours: PLAY_SESSION_DEFAULT_HOURS,
    localToken: false,
  };
  if (!wallet || wallet.length < 32) return empty;

  const local = readPlaySession(wallet);
  if (!local?.token) {
    return { ...empty, localToken: false };
  }

  try {
    const r = await fetch(
      `/api/play/session?wallet=${encodeURIComponent(wallet)}`,
      { headers: playSessionHeaders(wallet) },
    );
    const j = (await r.json()) as {
      active?: boolean;
      exp?: number;
      remainingSec?: number;
      ttlDefaultHours?: number;
    };
    if (j.active && j.exp) {
      writePlaySession({ wallet, token: local.token, exp: j.exp });
      return {
        active: true,
        exp: j.exp,
        remainingSec: j.remainingSec ?? Math.max(0, Math.floor((j.exp - Date.now()) / 1000)),
        ttlDefaultHours: j.ttlDefaultHours ?? PLAY_SESSION_DEFAULT_HOURS,
        localToken: true,
      };
    }
    // Server rejected — clear stale
    clearPlaySession();
    return {
      ...empty,
      ttlDefaultHours: j.ttlDefaultHours ?? PLAY_SESSION_DEFAULT_HOURS,
      localToken: false,
    };
  } catch {
    // Offline: trust local until exp
    return {
      active: true,
      exp: local.exp,
      remainingSec: Math.max(0, Math.floor((local.exp - Date.now()) / 1000)),
      ttlDefaultHours: PLAY_SESSION_DEFAULT_HOURS,
      localToken: true,
    };
  }
}

export type LeaveTableResult = {
  success: boolean;
  cashedOut: number;
  balance: number;
  error?: string;
};

/**
 * Leave table → chips return to play ledger (server SSOT).
 * No Web3 sign — play session not required for leave.
 */
export async function leaveTableToPlay(
  tableId: string,
  wallet: string,
): Promise<LeaveTableResult> {
  const r = await fetch(`/api/tables/${encodeURIComponent(tableId)}/leave`, {
    method: "POST",
    headers: playSessionHeaders(wallet, {
      "Content-Type": "application/json",
    }),
    body: JSON.stringify({ walletAddress: wallet, wallet }),
  });
  const j = (await r.json()) as {
    success?: boolean;
    cashedOut?: number;
    balance?: number;
    error?: string;
    message?: string;
  };
  if (!r.ok) {
    return {
      success: false,
      cashedOut: 0,
      balance: 0,
      error: j.error || j.message || "Leave failed",
    };
  }
  return {
    success: Boolean(j.success),
    cashedOut: Number(j.cashedOut) || 0,
    balance: Number(j.balance) || 0,
  };
}

/** Games that debit/credit play ledger (not fleet bag). */
export type PlayGameKind = "table" | "slots" | "blackjack";

export function enterPlayGameBlurb(kind: PlayGameKind): string {
  switch (kind) {
    case "table":
      return "Buy-in moves your real play GBUX onto the table. Leave table to return that stack to play (no new signature if your 24h play session is active).";
    case "slots":
      return "Each spin debits real play GBUX; wins credit the same ledger. Leaving the page is not a cash-out — balance stays on the dApp server.";
    case "blackjack":
      return "Bets move real play GBUX to the BUDBai agent house bank; wins pay from that bank. Fund via wallet transfer or $0.001/GBUX purchase.";
    default:
      return "Play GBUX is real product GBUX held for games on poker.grudge-studio.com.";
  }
}
